import {parseClipboardAsTable} from 'util/PasteUtil'
import Util from 'util/util'

/**
 * Transforms input table data into a table which is rectangular, meaning it each row has the
 * same number of columns and visa-versa. This prevents Table from rendering a "ragged" table
 * where missing empty cells make other cells allign incorrectly.
 *
 * @param {Object} tableData        original table data object
 * @returns {Object} transformed    tableData object
 */
export function getRectangularTableData(tableData) {
    const numberOfColumns = getNumberOfColumns(tableData)
    const tableRows = []
    for (const cell of tableData) {
        // For the total number of columns, create a cell for each in the current row,
        // then replace this cell with one from the data if present at that row and column index.
        if (Array.isArray(tableRows[cell.rowIndex]) === false) {
            tableRows[cell.rowIndex] = _.times(numberOfColumns, (columnIndex) => {
                return {
                    rowIndex: cell.rowIndex,
                    columnIndex,
                }
            })
        }
        tableRows[cell.rowIndex][cell.columnIndex] = cell
    }
    return _.sortBy(_.sortBy(_.flatten(tableRows).filter(Boolean), 'columnIndex'), 'rowIndex')
}

/**
 * Get a cell from the data representation of the table
 *
 * @param {Number} rowIndex row
 * @param {Number} columnIndex column
 * @returns {Object} cell data object
 */
export function getCell(tableData, rowIndex, columnIndex) {
    // Allow loose comparision since the indexes in the response may be saved as strings instead of numbers.
    // eslint-disable-next-line eqeqeq
    return tableData.find(c => c.rowIndex == rowIndex && c.columnIndex == columnIndex)
}

export function getNumberOfRows(tableData) {
    return _.uniq(tableData, 'rowIndex').length
}

export function getNumberOfColumns(tableData) {
    return _.uniq(tableData, 'columnIndex').length
}

export function updateRowIndices(tableData, insertRowAtIndex) {
    // Increment all table rows past the insertion point by 1.
    return tableData.map((cell) => {
        if (cell.rowIndex >= insertRowAtIndex) {
            cell.rowIndex++
        }
        return cell
    })
}

export function updateColumnIndices(tableData, insertColumnAtIndex) {
    // Increment all table columns past the insertion point by 1.
    return tableData.map((cell) => {
        if (cell.columnIndex >= insertColumnAtIndex) {
            cell.columnIndex++
        }
        return cell
    })
}

export function insertRow(tableData, insertRowAtIndex, rowToCopy = []) {
    tableData = updateRowIndices(tableData, insertRowAtIndex)
    // Insert table cells at the new row.
    // Copy gap cell state at the corresponding column indexes from selected row, if present.
    for (let columnIndex = 0; columnIndex < getNumberOfColumns(tableData); columnIndex++) {
        const gapCellToCopy = rowToCopy.find((cell) => cell.columnIndex === columnIndex && cell.isGapCell)
        tableData.push({
            cellText: '',
            ...gapCellToCopy,
            columnIndex,
            rowIndex: insertRowAtIndex,
            isGapCell: !!gapCellToCopy,
        })
    }
    return tableData
}

export function insertColumn(tableData, insertColumnAtIndex) {
    tableData = updateColumnIndices(tableData, insertColumnAtIndex)
    // Insert table cells at the new column.
    for (let rowIndex = 0; rowIndex < getNumberOfRows(tableData); rowIndex++) {
        tableData.push({
            columnIndex: insertColumnAtIndex,
            rowIndex,
            cellText: '',
            isGapCell: false
        })
    }
    return tableData
}

export function removeRow(tableData, rowIndex) {
    if (getNumberOfRows(tableData) > 1) {
        // Delete all cells of the selected row, then shift all cells below it one place to the top.
        return tableData.filter((cell) => {
            return cell.rowIndex !== rowIndex
        }).map((cell) => {
            if (cell.rowIndex > rowIndex) {
                cell.rowIndex--
            }
            return cell
        })
    }
    return tableData
}

export function removeColumn(tableData, columnIndex) {
    if (getNumberOfColumns(tableData) > 1) {
        // Delete all cells of the selected column, then shift all cells to the right of it one place to the left.
        return tableData.filter((cell) => {
            return cell.columnIndex !== columnIndex
        }).map((cell) => {
            if (cell.columnIndex > columnIndex) {
                cell.columnIndex--
            }
            return cell
        })
    }
    return tableData
}

/**
 * Remove empty rows and columns from the table, where an emtpy cell is defined as non-gap cell
 * with no content or only whitespace.
 */
export function removeEmptyRowsAndColumns(tableData) {
    // Iterate over each row from bottom to top so that no row gets skipped
    // when the one above it gets removed.
    _.reduceRight(_.groupBy(tableData, 'rowIndex'), (m, row, rowIndex) => {
        if (row.every(isEmptyCell)) {
            m.push(parseInt(rowIndex))
        }
        return m
    }, []).forEach((rowIndex) => {
        tableData = removeRow(tableData, rowIndex)
    })
    // Iterate over each column from right to left so that no column gets skipped
    // when the one to the left of it gets removed.
    _.reduceRight(_.groupBy(tableData, 'columnIndex'), (m, column, columnIndex) => {
        if (column.every(isEmptyCell)) {
            m.push(parseInt(columnIndex))
        }
        return m
    }, []).forEach((columnIndex) => {
        tableData = removeColumn(tableData, columnIndex)
    })

    return tableData
}

export function isEmptyCell(cell) {
    if (cell.isGapCell) {
        return false
    }
    let cellText = cell.cellText
    if (!cellText) {
        return true
    }
    cellText = Util.normalizeCharacters(cellText)
    if (cellText.length === 0) {
        return true
    }
    if (Util.normalizeCharacters(Util.stripTags(cellText)).length === 0 && !$(cellText).hasClass('js-expression')) {
        return true
    }
    return false
}

/**
 * Create HTML representation from table data
 *
 * @param {Object} tableData        table data object
 * @param {Array} columnWidths      optional array of custom column widths
 * @returns {String} Template to become the DOM representation of the table data
 */
export function generateTemplate(Template, Styles, tableData, columnWidths = []) {
    const numberOfColumns = getNumberOfColumns(tableData)
    const headerColumns = _.times(numberOfColumns, (columnIndex) => {
        let columnWidth = columnWidths[columnIndex] || {}
        if (columnWidth.percentage) {
            columnWidth = `${columnWidth.percentage}%`
        } else if (columnWidth.px) {
            columnWidth = `${columnWidth.px}px`
        } else {
            columnWidth = false
        }
        return {
            columnIndex,
            columnWidth
        }
    })

    return Template({
        Styles,
        tableRows: Object.values(_.groupBy(tableData, 'rowIndex')),
        headerColumns,
        cid: _.uniqueId(),
    })
}

/**
 * Checks if current selection range contains an element of query selector string or if the
 * table cell the selection is contained in has a class of a certain type.
 *
 * @param {String|null} targetSelector
 * Optional element selector string
 * @param {String} cellStyle
 * Class name which could be applied to the cell element.
 * @returns {Boolean} If current selected range or table cell contains the given element or cell class
 */
export function matchElement(targetSelector, cellStyle, TemplateStyles) {
    // Is currently focussed element inside of our table element and is it a table cell (TD) element.
    if (document.activeElement.nodeName === 'TD') {
        const cellElement = $(document.activeElement)
        // Check if the whole cell has a styling class applied to it.
        if (cellStyle && cellElement.is(`.${TemplateStyles[cellStyle]}`)) {
            return true
        }
        if (targetSelector) {
            if (cellElement.is(targetSelector)) {
                return true
            }
            const range = document.getSelection().getRangeAt(0)
            // Check if any container which is the parent of all selected nodes (commonAncestorContainer)
            // has a parent that matches our selector query within the scope of the table cell element.
            if ($(range.commonAncestorContainer).closest(targetSelector, cellElement).length > 0) {
                return true
            }
            // Check any nodes are present with for the given selector within the current selection.
            return range.cloneContents().querySelector(targetSelector) !== null
        }
    }
    return false
}

/**
 * Replace the selected text with the give parameter
 * Based on: http://stackoverflow.com/a/6252893
 *
 * @param  {string} html            HTML string to replace selection
 * @param  {Element} cellElement     last active cell element
 */
export function replaceSelectionWithHTML(html, cellElement) {
    cellElement.focus()
    document.execCommand('insertHTML', false, html)
}

export function attemptToPasteDataAsTable(tableData, pasteEvent) {
    // If all cells are empty and a rectangular table is pasted, replace the whole table
    const cellsWithText = tableData.filter(cell => cell.cellText?.length > 0)
    if (cellsWithText.length === 0) {
        const pastedTable = parseClipboardAsTable(pasteEvent, true, false)
        if (pastedTable) {
            const newTableData = []
            pastedTable.forEach((row, rowIndex) => {
                row.forEach((cellText, columnIndex) => {
                    newTableData.push({
                        rowIndex,
                        columnIndex,
                        cellText,
                        isGapCell: false,
                    })
                })
            })
            if (newTableData.length) {
                return getRectangularTableData(newTableData)
            }
        }
    }
    return false
}
